/**
 * Promise-based confirmation dialog provider.
 *
 * Replaces native window.confirm() calls with the branded ConfirmationModal so
 * destructive/unsaved-change prompts look consistent across every module.
 *
 * Setup (once, near the app root):
 *   <ConfirmProvider><App /></ConfirmProvider>
 *
 * Usage in any component:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete transaction',
 *     body: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     confirmVariant: 'danger',
 *   });
 *   if (!ok) return;
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmationModal from './ConfirmationModal';

const ConfirmContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Please confirm',
  body: 'Are you sure?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmVariant: 'danger',
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ show: false, options: DEFAULT_OPTIONS });
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    setState((prev) => ({ ...prev, show: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      // If a previous prompt is still pending, resolve it as cancelled.
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
      setState({ show: true, options: { ...DEFAULT_OPTIONS, ...options } });
    });
  }, []);

  const { show, options } = state;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmationModal
        show={show}
        onHide={() => settle(false)}
        onConfirm={() => settle(true)}
        title={options.title}
        body={options.body}
        confirmText={options.confirmText}
        confirmVariant={options.confirmVariant}
        cancelText={options.cancelText}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * Returns a confirm(options) => Promise<boolean> function.
 * Falls back to window.confirm if used outside a ConfirmProvider so callers
 * never crash during incremental adoption.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (ctx) return ctx;
  return (options = {}) =>
    Promise.resolve(
      window.confirm(
        [options.title, typeof options.body === 'string' ? options.body : '']
          .filter(Boolean)
          .join('\n\n') || 'Are you sure?'
      )
    );
}

export default ConfirmProvider;
